import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Breadcrumb, Icon, Table, Button, Message, Segment, Label } from 'semantic-ui-react';
import Page from '../../../components/Page';
import { ConnectButton } from '360dialog-connect-button';
import MyMenu from '../menu';
import { get, post } from '../../../util/api';

function useDialog360Integrations() {
	const [integrations, setIntegrations] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	const refresh = useCallback(async () => {
		setLoading(true);
		try {
			// get() already resolves to resp.data (the server JSON)
			const data = await get('/dahi/channel/get/dialog360cloud');
			setIntegrations((data && data.result) || []);
			setError(null);
		} catch (e) {
			setError(e.message);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		refresh();
	}, [refresh]);

	return { integrations, loading, error, refresh };
}

function IntegrationRow({ item }) {
	const { t } = useTranslation();
	const isActive = !!item.accessToken;
	const config = (item.data && item.data.config) || {};
	const waba_gen_id = config.waba_gen_id;

	return (
		<Table.Row>
			<Table.Cell>
				<Icon name="whatsapp" color="green" />
				{item.shop}
			</Table.Cell>
			<Table.Cell>
				{isActive ? (
					<Label color="green" size="small">
						{t('connected') || 'Bağlı'}
					</Label>
				) : (
					<Label color="grey" size="small">
						{t('disconnected') || 'Bağlı Değil'}
					</Label>
				)}
			</Table.Cell>
			<Table.Cell>{waba_gen_id || '-'}</Table.Cell>
			<Table.Cell textAlign="right">
				{waba_gen_id ? (
					<Button as={Link} to={`/settings/integrations/dialog360templates?waba_gen_id=${waba_gen_id}&shop=${encodeURIComponent(item.shop || '')}`} color="teal" size="small">
						<Icon name="clipboard list" />
						{t('templates') || 'Şablonlar'}
					</Button>
				) : (
					<Label basic size="small" color="grey">
						{t('notConnectedYet') || 'Henüz bağlı değil'}
					</Label>
				)}
			</Table.Cell>
		</Table.Row>
	);
}

const IS_MOCK = process.env.REACT_APP_DIALOG360_MOCK === 'true';

const MOCK_CALLBACK = { client: 'AqdZvKrzCL', channels: ['zkGg0zCH'] };

function ConnectArea({ onCallback, saving }) {
	const { t } = useTranslation();

	if (saving) {
		return (
			<div style={{ textAlign: 'center', padding: '20px' }}>
				<Icon name="asterisk" loading size="large" />
				<p style={{ marginTop: 8 }}>{t('saving') || 'Kaydediliyor...'}</p>
			</div>
		);
	}

	return (
		<div style={{ textAlign: 'center', padding: '20px' }}>
			<p style={{ color: '#666', marginBottom: 12 }}>
				{t('dialog360ConnectDesc') || 'Yeni bir 360Dialog WhatsApp numarası bağlamak için butona tıklayın.'}
			</p>
			{IS_MOCK ? (
				<Button color="orange" onClick={() => onCallback(MOCK_CALLBACK)}>
					[Mock] Connect
				</Button>
			) : (
				<ConnectButton partnerId={'wBRCxXPA'} callback={onCallback} />
			)}
		</div>
	);
}

export default function Dialog360(props) {
	const { t } = useTranslation();
	const { integrations, loading, error: loadError, refresh } = useDialog360Integrations();
	const [saving, setSaving] = useState(false);
	const [saveError, setSaveError] = useState(null);
	const [showConnect, setShowConnect] = useState(false);
	const [lastCallback, setLastCallback] = useState(null);

	const handleCallback = useCallback(
		async (callbackObject) => {
			const { client, channels } = callbackObject || {};
			setLastCallback(callbackObject);
			if (!channels || !channels.length) return;
			setSaving(true);
			setSaveError(null);
			try {
				for (const channel_id of channels) {
					// post() returns full axios response; server JSON is in .data
					const resp = await post('/dahi/channel/save', { channel: 'dialog360cloud', data: { channel_id, client_id: client } });
					if (!resp || !resp.data || !resp.data.success) {
						throw new Error((resp && resp.data && resp.data.message) || 'Kayıt başarısız');
					}
				}
				setShowConnect(false);
				await refresh();
			} catch (e) {
				setSaveError(e.message);
			} finally {
				setSaving(false);
			}
		},
		[refresh]
	);

	return (
		<Page
			header={
				<Breadcrumb size="big">
					<Breadcrumb.Section as={Link} to={`/settings`}>
						<Icon name="setting" />
						{t('settings')}
					</Breadcrumb.Section>
					<Breadcrumb.Divider icon="right chevron" />
					<Breadcrumb.Section as={Link} to={`/settings/integrations`}>
						{t('integrations')}
					</Breadcrumb.Section>
					<Breadcrumb.Divider icon="right chevron" />
					<Breadcrumb.Section>360Dialog WhatsApp</Breadcrumb.Section>
				</Breadcrumb>
			}
			extra={
				<div className="ml-auto">
					<Button primary onClick={() => setShowConnect((v) => !v)}>
						<Icon name="plus" />
						{t('addNew') || 'Yeni Ekle'}
					</Button>
				</div>
			}
			flex
		>
			<MyMenu activeItem="">
				<Page.Wrap>
					{loadError && (
						<Message negative>
							<Message.Header>Hata</Message.Header>
							<p>{loadError}</p>
						</Message>
					)}

					{saveError && (
						<Message negative onDismiss={() => setSaveError(null)}>
							<Message.Header>Bağlama Hatası</Message.Header>
							<p>{saveError}</p>
						</Message>
					)}

					{showConnect && (
						<Segment>
							<ConnectArea onCallback={handleCallback} saving={saving} />
						</Segment>
					)}

					{lastCallback && (
						<Segment>
							<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
								<strong style={{ fontSize: 12, color: '#888' }}>Callback JSON</strong>
								<Button size="mini" basic icon="close" onClick={() => setLastCallback(null)} />
							</div>
							<pre style={{ background: '#f5f5f5', padding: '10px', borderRadius: 4, fontSize: 12, margin: 0, overflowX: 'auto' }}>
								{JSON.stringify(lastCallback, null, 2)}
							</pre>
						</Segment>
					)}

					{loading ? (
						<div style={{ textAlign: 'center', padding: '30px' }}>
							<Icon name="asterisk" loading size="large" />
						</div>
					) : integrations.length === 0 ? (
						<Message info style={{ textAlign: 'center' }}>
							<p>{t('noConnectionFound') || 'Bağlı 360Dialog numarası bulunamadı. "Yeni Ekle" ile bağlayabilirsiniz.'}</p>
						</Message>
					) : (
						<Table>
							<Table.Header>
								<Table.Row>
									<Table.HeaderCell>{t('phoneNumber') || 'Telefon Numarası'}</Table.HeaderCell>
									<Table.HeaderCell>{t('status') || 'Durum'}</Table.HeaderCell>
									<Table.HeaderCell>Waba ID</Table.HeaderCell>
									<Table.HeaderCell textAlign="right">{t('actions') || 'İşlemler'}</Table.HeaderCell>
								</Table.Row>
							</Table.Header>
							<Table.Body>
								{integrations.map((item) => (
									<IntegrationRow
										key={item.id}
										item={item}
									/>
								))}
							</Table.Body>
						</Table>
					)}
				</Page.Wrap>
			</MyMenu>
		</Page>
	);
}
